"use client";

import React from "react";
import { ShieldCheck, Building2, GitMerge, FileCheck2, Lock, HardHat, ArrowRight, CircleCheck as CheckCircle2, TriangleAlert as AlertTriangle, FileSpreadsheet } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const complianceCards = [{
  title: "Secure-site access protocols",
  subtitle: "Entry control & boundary protection",
  icon: Lock,
  badge: "Site Security",
  points: ["CSCS/CISRS verified erectors with site-specific safety briefings on arrival", "Tamper-resistant ladder guards and lockable ground-level access gates", "Perimeter debris netting, monarflex sheeting, and physical boundary hoarding", "Secure keying and restricted out-of-hours climb prevention systems"]
}, {
  title: "External envelope & plant-room access",
  subtitle: "Complex geometry & critical building services",
  icon: Building2,
  badge: "Structural Access",
  points: ["Bespoke birdcage, cantilever, and bridged access around rooftop plant equipment", "High-clearance gantries to maintain ventilation, emergency egress, and supply lines", "Non-intrusive base-tying methods to preserve delicate cladding and membrane finishes", "Point-load distribution calculations for flat roofs, terraces, and soft ground"]
}, {
  title: "Phased handover on occupied sites",
  subtitle: "Zero disruption for live tenancies & public paths",
  icon: GitMerge,
  badge: "Live Operations",
  points: ["Segmented erection sequences mapped around building occupant schedules", "Heavy-duty crash decks and covered pedestrian fan walkways at entry doors", "Section-by-section Scafftag issue so trade work starts without waiting for full strikes", "Planned quiet-working periods for residential, educational, and commercial units"]
}, {
  title: "NASC, CHAS & safety standards",
  subtitle: "Statutory inspections & verified accreditation",
  icon: ShieldCheck,
  badge: "Verified Compliance",
  points: ["Full adherence to NASC TG20:21 compliance sheets and SG4:22 fall prevention", "CHAS and Constructionline accredited quality, health, and environmental processes", "Pre-commencement site-specific RAMS (Risk Assessment & Method Statements)", "Mandatory 7-day statutory safety inspections with signed digital records provided"]
}];
const accreditationBadges = [{
  label: "NASC Criteria",
  desc: "TG20:21 & SG4:22 guidelines"
}, {
  label: "CHAS Accredited",
  desc: "Health & safety management"
}, {
  label: "Constructionline",
  desc: "Pre-qualified UK contractor"
}, {
  label: "CISRS Qualified",
  desc: "Trained scaffolding personnel"
}];
export default function SiteCompliance() {
  const prefersReducedMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: prefersReducedMotion ? {
      opacity: 1
    } : {
      opacity: 0,
      y: 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="siteaccesscompliance" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="max-w-3xl mb-12 sm:mb-16">
          <Badge variant="outline" className="mb-4 text-xs font-semibold uppercase tracking-wider">
            Operational Standards
          </Badge>
          <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
            Site access and safety protocols
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Every scaffold built by BMF Scaffolding Ltd follows strict access controls, structured handovers, and independently verified NASC, CHAS, and Constructionline standards.
          </p>
        </div>

        {/* 4-Card Protocol Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-14">
          {complianceCards.map((card, i) => {
          const IconComponent = card.icon;
          return <motion.div key={card.title} variants={itemVariants} data-index={i}>
                <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-200">
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <div className="inline-flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <IconComponent className="size-5" />
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {card.badge}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl sm:text-2xl font-bold text-card-foreground">
                      {card.title}
                    </CardTitle>
                    <CardDescription className="text-sm text-muted-foreground">
                      {card.subtitle}
                    </CardDescription>
                  </CardHeader>
                  <Separator className="mx-6 w-auto opacity-50" />
                  <CardContent className="pt-5">
                    <ul className="space-y-3.5">
                      {card.points.map((point, pointIndex) => <li key={pointIndex} data-index={pointIndex} className="flex items-start gap-2.5 text-sm text-card-foreground leading-snug">
                          <CheckCircle2 className="size-4 shrink-0 text-primary mt-0.5" />
                          <span>{point}</span>
                        </li>)}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Accreditation Ribbon & Direct Actions */}
        <div className="rounded-xl border border-border bg-muted/40 p-6 sm:p-8 lg:p-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7 space-y-4">
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <FileCheck2 className="size-4 text-primary" />
                <span>Audited Contractor Verification</span>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-foreground">
                Rigorous compliance for main contractors, developers, and councils
              </h3>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                We supply full RAMS, scaffolding designs, handover certificates, and 7-day statutory inspection tags prior to handover, keeping your site audit-ready.
              </p>

              {/* Accreditations Row */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
                {accreditationBadges.map((badge, idx) => <div key={badge.label} data-index={idx} className="rounded-lg border border-border bg-card p-3">
                    <div className="text-xs font-bold text-foreground">{badge.label}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5 leading-tight">{badge.desc}</div>
                  </div>)}
              </div>
            </div>

            <div className="lg:col-span-5 flex flex-col sm:flex-row lg:flex-col gap-3 justify-end lg:border-l lg:border-border lg:pl-8">
              <Button asChild size="lg" className="w-full justify-center">
                <Link to="/ContactRequestQuotePage">
                  <span>Request site quote</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full justify-center">
                <Link to="/HealthSafetyAccreditationsPage">
                  <span>View safety documentation</span>
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}